import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/AdminKeyRotation.css';

function AdminKeyRotation() {
  const [adminToken, setAdminToken] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rotationData, setRotationData] = useState({
    batch_size: 100,
    new_encryption_key: '',
    new_index_key: ''
  });
  const [autoGenerate, setAutoGenerate] = useState(true);

  useEffect(() => {
    // Try to load admin token from localStorage
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setAdminToken(savedToken);
    }
  }, []);

  const loadStatus = async () => {
    if (!adminToken) {
      setError('Please enter an admin token first');
      return;
    }

    try {
      setStatusLoading(true);
      setError('');
      const response = await api.getRotationStatus(adminToken);
      setStatus(response);
      // Save token for future use
      localStorage.setItem('admin_token', adminToken);
    } catch (err) {
      setError('Failed to load status: ' + err.message);
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleRotate = async () => {
    if (!adminToken) {
      setError('Please enter an admin token');
      return;
    }

    if (!window.confirm('⚠️ WARNING: Key rotation is a critical operation that will re-encrypt all data. Make sure you have a backup. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const data = {
        batch_size: parseInt(rotationData.batch_size)
      };

      if (!autoGenerate) {
        if (!rotationData.new_encryption_key || !rotationData.new_index_key) {
          setError('Please provide both encryption and index keys');
          return;
        }
        data.new_encryption_key = rotationData.new_encryption_key;
        data.new_index_key = rotationData.new_index_key;
      }

      const response = await api.rotateEncryptionKeys(adminToken, data);
      
      setSuccess(`
        Key rotation completed successfully!
        - Records processed: ${response.records_processed}/${response.records_total}
        - Duration: ${response.duration_seconds} seconds
        - Config updated: ${response.config_updated ? 'Yes' : 'No'}
        - Email sent: ${response.email_sent ? 'Yes' : 'No'}
        ${response.backup_file ? `\n- Backup file: ${response.backup_file}` : ''}
      `);

      // Show new keys (in production, this should be handled more securely)
      console.log('New keys generated:', {
        encryption_key: response.new_encryption_key,
        index_key: response.new_index_key
      });

      // Reload status
      loadStatus();
    } catch (err) {
      setError('Key rotation failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRotationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="admin-key-rotation-container">
      <div className="rotation-header">
        <h1>🔐 Admin Key Rotation</h1>
        <p className="warning-text">
          ⚠️ This is a critical admin operation. Use with caution.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="rotation-content">
        <div className="token-section">
          <h2>Admin Token</h2>
          <div className="form-group">
            <label>Admin API Token</label>
            <input
              type="password"
              value={adminToken}
              onChange={(e) => setAdminToken(e.target.value)}
              placeholder="Enter admin token"
              className="token-input"
            />
            <small>Required for all operations on this page</small>
          </div>
          <button 
            className="btn-secondary" 
            onClick={loadStatus}
            disabled={statusLoading || !adminToken}
          >
            {statusLoading ? 'Loading...' : 'Load Status'}
          </button>
        </div>

        {status && (
          <div className="status-section">
            <h2>Current Status</h2>
            <div className="status-grid">
              <div className="status-card">
                <h3>Total Encrypted Records</h3>
                <p className="status-value">{status.total_encrypted_records}</p>
              </div>
              <div className="status-card">
                <h3>Encryption Key Set</h3>
                <p className={`status-value ${status.encryption_key_set ? 'status-ok' : 'status-error'}`}>
                  {status.encryption_key_set ? '✓ Yes' : '✗ No'}
                </p>
              </div>
              <div className="status-card">
                <h3>Index Key Set</h3>
                <p className={`status-value ${status.index_key_set ? 'status-ok' : 'status-error'}`}>
                  {status.index_key_set ? '✓ Yes' : '✗ No'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rotation-section">
          <h2>Key Rotation Settings</h2>
          
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={autoGenerate}
                onChange={(e) => setAutoGenerate(e.target.checked)}
              />
              <span>Auto-generate new keys (Recommended)</span>
            </label>
          </div>

          {!autoGenerate && (
            <>
              <div className="form-group">
                <label>New Encryption Key</label>
                <input
                  type="text"
                  name="new_encryption_key"
                  value={rotationData.new_encryption_key}
                  onChange={handleChange}
                  placeholder="Enter new encryption key (base64)"
                />
              </div>

              <div className="form-group">
                <label>New Index Key</label>
                <input
                  type="text"
                  name="new_index_key"
                  value={rotationData.new_index_key}
                  onChange={handleChange}
                  placeholder="Enter new index key (base64)"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Batch Size</label>
            <input
              type="number"
              name="batch_size"
              value={rotationData.batch_size}
              onChange={handleChange}
              min="1"
              max="1000"
            />
            <small>Number of records to process per batch (default: 100)</small>
          </div>
        </div>

        <div className="rotation-actions">
          <div className="warning-box">
            <svg className="warning-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <strong>Before rotating keys:</strong>
              <ul>
                <li>Ensure you have a complete database backup</li>
                <li>Plan for potential downtime</li>
                <li>Notify all system administrators</li>
                <li>The process cannot be reversed once started</li>
                <li>New keys will be sent to admin email if configured</li>
              </ul>
            </div>
          </div>

          <button 
            className="btn-danger btn-large"
            onClick={handleRotate}
            disabled={loading || !adminToken}
          >
            {loading ? 'Rotating Keys...' : 'Rotate Encryption Keys'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminKeyRotation;

